import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../turkeydrop/turkeydrop2025/turkeydrop2025.css";
import ShieldIMG from "../../assets/rhp-shield.png";
import ShieldFooterIMG from "../../assets/shield.png";
import { supabase } from "../../utils/supabaseClient";
import { mergeAttributionIntoPayload } from "../../utils/attributionPersistence";
import { generateTrackingId } from "./tracking";
import SdTeamSection from "./components/SdTeamSection";
import ClosedEventMessage from "../openhouse/ClosedEventMessage";
import AddToCalendar from "../events/AddToCalendar";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const STATUS_OPTIONS = [
  "Veteran",
  "Active Duty",
  "Guard/Reserve",
  "Family Member/Caregiver",
  "Provider/Community Partner",
  "Other",
];

const SERVICE_STATUSES = new Set(["Veteran", "Active Duty", "Guard/Reserve"]);

const BRANCHES = [
  "Army",
  "Army National Guard",
  "Navy",
  "Marine Corps",
  "Air Force",
  "Coast Guard",
  "Space Force",
];

const ERAS = ["Pre-9/11", "Post-9/11"];

function initialAttendee() {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    digits: "",
    status: "Veteran",
    branches: [],
    eras: [],
  };
}

function MultiChipGroup({ label, options, values, onToggle, idPrefix }) {
  return (
    <div className="tdp-field">
      <div className="tdp-chips-head">{label}</div>
      <div className="chip-grid" role="group" aria-label={label}>
        {options.map((opt) => {
          const pressed = values.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              className="chip-btn"
              aria-pressed={pressed}
              onClick={() => onToggle(opt)}
              id={`${idPrefix}-${opt.replace(/\W+/g, "-").toLowerCase()}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function SdRsvpPage({
  event,
  topLabel = "Road Home Program San Diego Events",
  thankYouPath = "/sd/thank-you",
  showTeamSection = true,
  unavailableLabel = "More details coming soon",
  unavailableMessage = "RSVP details are not available for this event yet. Please check back soon.",
  unavailableCtaHref = "",
  unavailableCtaLabel = "",
}) {
  const navigate = useNavigate();
  const safeEvent = event || {};
  const isSaluteSocialPage = String(safeEvent.slug || "").startsWith("salute-social-");
  const isEventClosed = !!safeEvent.isClosed;
  const isMultiMode = safeEvent.mode === "multi";
  const isFamilyMode = safeEvent.mode === "family";
  const isHostedTicketed = safeEvent.ticketing === "hosted";
  const hostedTicketGuestsUseTicketFieldsOnly =
    isHostedTicketed && safeEvent.hostedTicketGuestFields === "name-email";
  const formStatusOptions = Array.isArray(safeEvent.statusOptions) && safeEvent.statusOptions.length
    ? safeEvent.statusOptions
    : STATUS_OPTIONS;
  const maxAttendees = Math.max(1, safeEvent.limits?.maxAttendees || 1);
  const maxPartySize = Math.max(1, safeEvent.limits?.maxPartySize || 4);
  const [resolvedSlotId, setResolvedSlotId] = useState(safeEvent.slot_id || "");
  const [slotLookupLoading, setSlotLookupLoading] = useState(false);
  const effectiveSlotId = safeEvent.slot_id || resolvedSlotId;
  const hasCapacityTracking = Number.isFinite(safeEvent.limits?.capacityTotal) && !!effectiveSlotId;

  const [attendees, setAttendees] = useState([initialAttendee()]);
  const [partySize, setPartySize] = useState(1);
  const [consent, setConsent] = useState(false);
  const [addToMailingList, setAddToMailingList] = useState(
    safeEvent.defaultMailingListOptIn !== false
  );

  const [message, setMessage] = useState("");
  const [messageCode, setMessageCode] = useState("");
  const [ticketConfirmation, setTicketConfirmation] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [scriptReady, setScriptReady] = useState(false);
  const [widgetId, setWidgetId] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [slotCapacity, setSlotCapacity] = useState(
    Number.isFinite(safeEvent.limits?.capacityTotal) ? Number(safeEvent.limits.capacityTotal) : null
  );
  const [seatsTaken, setSeatsTaken] = useState(null);
  const [seatsRemaining, setSeatsRemaining] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacityLoadError, setCapacityLoadError] = useState("");

  useEffect(() => {
    if (safeEvent.title) document.title = safeEvent.title;
  }, [safeEvent.title]);

  useEffect(() => {
    if (!isSaluteSocialPage) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [isSaluteSocialPage, safeEvent.key]);

  useEffect(() => {
    setResolvedSlotId(safeEvent.slot_id || "");
  }, [safeEvent.slot_id, safeEvent.event_id]);

  useEffect(() => {
    setAddToMailingList(safeEvent.defaultMailingListOptIn !== false);
  }, [safeEvent.defaultMailingListOptIn, safeEvent.event_id]);

  useEffect(() => {
    let active = true;

    if (safeEvent.slot_id || !safeEvent.event_id || isEventClosed) {
      setSlotLookupLoading(false);
      return undefined;
    }

    const fetchFirstSlot = async () => {
      setSlotLookupLoading(true);
      const slotQuery = supabase?.from?.("v_slot_capacity");

      if (!slotQuery || typeof slotQuery.select !== "function") {
        setResolvedSlotId("");
        setSlotLookupLoading(false);
        return;
      }

      const { data, error } = await slotQuery
        .select("slot_id,label")
        .eq("event_id", safeEvent.event_id);

      if (!active) return;

      if (!error && Array.isArray(data) && data.length > 0) {
        const sorted = [...data].sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));
        const preferredLabel = String(safeEvent.slotLabel || "").trim().toLowerCase();
        const preferredSlot = preferredLabel
          ? sorted.find((slot) => String(slot?.label || "").trim().toLowerCase() === preferredLabel)
          : null;
        setResolvedSlotId(preferredSlot?.slot_id || sorted[0]?.slot_id || "");
      } else {
        setResolvedSlotId("");
      }

      setSlotLookupLoading(false);
    };

    fetchFirstSlot();

    return () => {
      active = false;
    };
  }, [safeEvent.slot_id, safeEvent.slotLabel, safeEvent.event_id, isEventClosed]);

  const loadCapacity = useCallback(async () => {
    if (!hasCapacityTracking) {
      setCapacityLoadError("");
      return;
    }

    setCapacityLoading(true);
    setCapacityLoadError("");

    let data;
    let error;

    if (isHostedTicketed) {
      const response = await supabase?.rpc?.("get_public_hosted_event_capacity", {
        p_event_id: safeEvent.event_id,
      });
      data = Array.isArray(response?.data) ? response.data[0] : response?.data;
      error = response?.error;
    } else {
      const response = await supabase
        ?.from?.("v_slot_capacity")
        ?.select?.("capacity, seats_taken, seats_remaining, is_full")
        ?.eq?.("slot_id", effectiveSlotId)
        ?.maybeSingle?.();
      data = response?.data;
      error = response?.error;
    }

    if (!data && !error) {
      setCapacityLoadError("Unable to load live seat availability right now.");
      setCapacityLoading(false);
      return;
    }

    if (error) {
      setCapacityLoadError("Unable to load live seat availability right now.");
      setCapacityLoading(false);
      return;
    }

    const fallbackCapacity = Number.isFinite(safeEvent.limits?.capacityTotal)
      ? Number(safeEvent.limits.capacityTotal)
      : null;

    const nextCapacity = Number(data?.capacity);
    const nextTaken = Number(isHostedTicketed ? data?.reserved : data?.seats_taken);
    const nextRemaining = Number(isHostedTicketed ? data?.remaining : data?.seats_remaining);

    setSlotCapacity(Number.isFinite(nextCapacity) ? nextCapacity : fallbackCapacity);
    setSeatsTaken(Number.isFinite(nextTaken) ? nextTaken : null);
    setSeatsRemaining(
      Number.isFinite(nextRemaining)
        ? Math.max(0, nextRemaining)
        : (Number.isFinite(fallbackCapacity) ? fallbackCapacity : null)
    );
    setCapacityLoading(false);
  }, [
    effectiveSlotId,
    safeEvent.event_id,
    safeEvent.limits?.capacityTotal,
    hasCapacityTracking,
    isHostedTicketed,
  ]);

  useEffect(() => {
    loadCapacity();
  }, [loadCapacity]);

  const totalSeats = Number.isFinite(slotCapacity) ? Number(slotCapacity) : null;
  const remainingSeats = Number.isFinite(seatsRemaining) ? Math.max(0, Number(seatsRemaining)) : null;
  const takenSeats = Number.isFinite(seatsTaken)
    ? Math.max(0, Number(seatsTaken))
    : (totalSeats !== null && remainingSeats !== null ? Math.max(0, totalSeats - remainingSeats) : null);
  const isSoldOut = hasCapacityTracking && remainingSeats !== null && remainingSeats <= 0;
  const isLimitedSpots = hasCapacityTracking && remainingSeats !== null && remainingSeats > 0 && remainingSeats <= 10;
  const isRsvpUnavailable = !isEventClosed && !effectiveSlotId && !slotLookupLoading;
  const canRenderRsvpForm = !isEventClosed && !slotLookupLoading && !isRsvpUnavailable;
  const maxPartyByRemaining = isFamilyMode && remainingSeats !== null
    ? Math.max(0, Math.min(maxPartySize, remainingSeats))
    : maxPartySize;
  const partySizeOverRemaining = isFamilyMode && remainingSeats !== null && partySize > maxPartyByRemaining;
  const pctFilled = totalSeats && takenSeats !== null
    ? Math.min(100, Math.max(0, Math.round((takenSeats / totalSeats) * 100)))
    : 0;

  useEffect(() => {
    if (!canRenderRsvpForm || !SITE_KEY) return;
    if (window.turnstile) {
      setScriptReady(true);
      return;
    }
    const existing = document.getElementById("cf-turnstile-script");
    if (existing) {
      let tries = 0;
      const interval = setInterval(() => {
        tries += 1;
        if (window.turnstile) {
          setScriptReady(true);
          clearInterval(interval);
          return;
        }
        if (tries >= 20) clearInterval(interval);
      }, 100);
      return () => clearInterval(interval);
    }
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => setScriptReady(true);
    document.head.appendChild(s);
    return () => {
      s.onload = null;
    };
  }, [canRenderRsvpForm]);

  useEffect(() => {
    if (!canRenderRsvpForm || !scriptReady || !window.turnstile || widgetId || !SITE_KEY) return;
    if (!document.getElementById("turnstile-container")) return;
    const id = window.turnstile.render("#turnstile-container", {
      sitekey: SITE_KEY,
      callback: setCaptchaToken,
      "expired-callback": () => setCaptchaToken(""),
      "error-callback": () => setCaptchaToken(""),
      retry: "auto",
    });
    setWidgetId(id);
  }, [canRenderRsvpForm, scriptReady, widgetId]);

  useEffect(() => {
    if (!captchaToken) return;
    const ev = new CustomEvent("cf-turnstile-token", {
      detail: { token: captchaToken },
    });
    document.dispatchEvent(ev);
  }, [captchaToken]);

  const getTurnstileToken = useCallback(async () => {
    if (!window.turnstile || !widgetId) return "";
    if (captchaToken) return captchaToken;
    return new Promise((resolve) => {
      const onToken = (e) => {
        resolve(e.detail.token);
        document.removeEventListener("cf-turnstile-token", onToken);
      };
      document.addEventListener("cf-turnstile-token", onToken);
      window.turnstile.execute(widgetId);
      setTimeout(() => {
        document.removeEventListener("cf-turnstile-token", onToken);
        resolve("");
      }, 3000);
    });
  }, [captchaToken, widgetId]);

  const refreshTurnstile = useCallback(() => {
    setCaptchaToken("");
    if (window.turnstile && widgetId) window.turnstile.reset(widgetId);
  }, [widgetId]);

  const formatPhone = (d) => {
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 10);
    if (d.length <= 3) return `(${a}`;
    if (d.length <= 6) return `(${a}) ${b}`;
    return `(${a}) ${b}-${c}`;
  };

  const isEmail = (v) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(v);

  const extractResponseFromError = async (errorObj) => {
    if (!errorObj?.context || typeof errorObj.context.json !== "function") {
      return null;
    }
    try {
      const parsed = await errorObj.context.json();
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // ignore parse failures
    }
    return null;
  };

  const updateAttendee = (index, updater) => {
    setAttendees((prev) =>
      prev.map((attendee, i) => (i === index ? updater(attendee) : attendee))
    );
  };

  const updateAttendeeField = (index, field, value) => {
    updateAttendee(index, (attendee) => ({ ...attendee, [field]: value }));
    const fieldKey = `attendees.${index}.${field}`;
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    }
  };

  const onPhoneChange = (index, value) => {
    const nextDigits = value.replace(/\D/g, "").slice(0, 10);
    updateAttendee(index, (attendee) => ({
      ...attendee,
      digits: nextDigits,
      phone: formatPhone(nextDigits),
    }));
    const fieldKey = `attendees.${index}.phone`;
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    }
  };

  const handleStatusChange = (index, nextStatus) => {
    const isServiceStatus = SERVICE_STATUSES.has(nextStatus);

    updateAttendee(index, (attendee) => ({
      ...attendee,
      status: nextStatus,
      branches: isServiceStatus ? attendee.branches : [],
      eras: isServiceStatus ? attendee.eras : [],
    }));

    setErrors((prev) => ({
      ...prev,
      [`attendees.${index}.status`]: undefined,
      [`attendees.${index}.branches`]: undefined,
      [`attendees.${index}.eras`]: undefined,
    }));
  };

  const toggleAttendeeChip = (index, field, option) => {
    updateAttendee(index, (attendee) => {
      const current = attendee[field];
      const next = current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option];
      return { ...attendee, [field]: next };
    });
    const fieldKey = `attendees.${index}.${field}`;
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    }
  };

  const addAttendee = () => {
    if (!isMultiMode || attendees.length >= maxAttendees) return;
    setAttendees((prev) => [...prev, initialAttendee()]);
  };

  const removeAttendee = (index) => {
    if (!isMultiMode || attendees.length <= 1) return;
    setAttendees((prev) => prev.filter((_, i) => i !== index));
    setErrors({});
  };

  const scrollToFirstError = (errs) => {
    const keys = Object.keys(errs).filter((key) => errs[key]);
    if (!keys.length) return;
    const firstKey = keys.sort()[0];
    const node = document.querySelector(`[data-field="${firstKey}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEventClosed || !effectiveSlotId) return;
    setMessage("");
    setMessageCode("");
    setSubmitting(true);

    const nextErrors = {};

    attendees.forEach((attendee, index) => {
      const prefix = `attendees.${index}`;
      const isServiceStatus = SERVICE_STATUSES.has(attendee.status);
      const requiresFullProfile = !hostedTicketGuestsUseTicketFieldsOnly || index === 0;
      if (!attendee.firstName.trim()) nextErrors[`${prefix}.firstName`] = "Required";
      if (!attendee.lastName.trim()) nextErrors[`${prefix}.lastName`] = "Required";
      if (!attendee.email.trim() || !isEmail(attendee.email.trim())) {
        nextErrors[`${prefix}.email`] = "Required";
      }
      if (requiresFullProfile && attendee.digits.length !== 10) {
        nextErrors[`${prefix}.phone`] = "Required";
      }
      if (requiresFullProfile && !attendee.status) nextErrors[`${prefix}.status`] = "Required";
      if (requiresFullProfile && isServiceStatus && !attendee.branches.length) {
        nextErrors[`${prefix}.branches`] = "Select at least one branch.";
      }
      if (requiresFullProfile && isServiceStatus && !attendee.eras.length) {
        nextErrors[`${prefix}.eras`] = `Attendee ${index + 1}: Select at least one era.`;
      }
    });

    if (isFamilyMode) {
      if (isSoldOut) {
        nextErrors.partySize = "This event is currently full.";
      } else if (partySizeOverRemaining && remainingSeats !== null) {
        nextErrors.partySize = `Only ${remainingSeats} seat${remainingSeats === 1 ? "" : "s"} remaining. Reduce party size to continue.`;
      } else if (!Number.isFinite(partySize) || partySize < 1 || partySize > maxPartySize) {
        nextErrors.partySize = `Party size must be between 1 and ${maxPartySize}.`;
      }
    }

    if (!consent) nextErrors.consent = "Required";

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setMessage("Please fix the highlighted fields and try again.");
      scrollToFirstError(nextErrors);
      setSubmitting(false);
      return;
    }

    let token = captchaToken;
    if (!token && window.turnstile && widgetId) token = await getTurnstileToken();
    if (!token) {
      setMessage("❌ Human verification failed. Please try again.");
      setSubmitting(false);
      return;
    }

    const orderId = generateTrackingId();
    const attendeePayload = attendees.map((attendee, index) => {
      const ticketFields = {
        attendee_id: generateTrackingId(),
        attendee_index: index,
        first_name: attendee.firstName.trim(),
        last_name: attendee.lastName.trim(),
        email: attendee.email.trim(),
      };

      if (hostedTicketGuestsUseTicketFieldsOnly && index > 0) return ticketFields;

      return {
        ...ticketFields,
        phone: attendee.phone,
        status: attendee.status,
        branch_of_service: SERVICE_STATUSES.has(attendee.status) ? attendee.branches : [],
        era_list: SERVICE_STATUSES.has(attendee.status) ? attendee.eras : [],
      };
    });

    // Submit all attendees in one request to prevent partial saves from per-attendee loops.
    const payload = mergeAttributionIntoPayload({
      event_id: safeEvent.event_id,
      slot_id: effectiveSlotId,
      order_id: orderId,
      attendees: attendeePayload,
      add_to_mailing_list: addToMailingList,
      interests: safeEvent.mailingListInterests || [`${safeEvent.title || "Event"} updates`],
      consent: true,
      cf_turnstile_token: token,
    }, window.location, document);
    if (isFamilyMode) payload.family_size = partySize;
    if (isHostedTicketed) {
      const primary = attendeePayload[0];
      Object.assign(payload, {
        first_name: primary.first_name,
        last_name: primary.last_name,
        email: primary.email,
        phone: primary.phone,
        military_affiliation: primary.status,
        branch_of_service: primary.branch_of_service,
        era_list: primary.era_list,
        party_size: attendeePayload.length,
        mailing_list_opt_in: addToMailingList,
      });
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        isHostedTicketed ? "reserve-rockin-rsvp" : "reserve-rsvp",
        {
          body: payload,
        }
      );

      let responseData = data && typeof data === "object" ? data : null;
      if (!responseData && error) {
        responseData = await extractResponseFromError(error);
      }

      if (error || responseData?.ok === false) {
        const code = responseData?.code || "";
        const serverMessage =
          responseData?.error ||
          error?.message ||
          "Something went wrong. Please review and try again.";
        setMessageCode(code);
        setMessage(serverMessage);
        if (code === "CAPACITY_REACHED" || code === "EVENT_FULL") {
          await loadCapacity();
        }
        refreshTurnstile();
        return;
      }

      if (responseData?.ok !== true) {
        setMessage("Something went wrong. Please email events@roadhomeprogram.org.");
        refreshTurnstile();
        return;
      }

      if (isHostedTicketed) {
        setTicketConfirmation(responseData);
        await loadCapacity();
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        return;
      }

      const params = new URLSearchParams({
        event: safeEvent.key,
        order_id: orderId,
        count: String(isFamilyMode ? partySize : attendees.length),
      });
      if (isFamilyMode) params.set("party_size", String(partySize));
      navigate(`${thankYouPath}?${params.toString()}`);
    } catch {
      setMessage("❌ Something went wrong. Please email events@roadhomeprogram.org.");
      refreshTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  if (!safeEvent || !safeEvent.event_id) return null;

  return (
    <>
      <main
        className={`tdp-shell ${isSaluteSocialPage ? "tdp-shell--sd-salute-layout" : ""}`.trim()}
      >
        <div
          className="tdp-bg"
          style={{ "--bg-url": `url(${ShieldIMG})` }}
          aria-hidden="true"
        />

        <section className="tdp-left">
          <p className="tdp-sub" style={{ margin: 0, fontWeight: 600 }}>
            {topLabel}
          </p>

          {safeEvent.webBanner ? (
            <div
              className="tdp-block"
              style={{ marginTop: 14, marginBottom: 18, padding: 6, overflow: "hidden" }}
            >
              <img
                src={safeEvent.webBanner}
                alt={`${safeEvent.venueName || safeEvent.title || "Event"} web banner`}
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  borderRadius: 12,
                  background: "#fff",
                }}
              />
            </div>
          ) : null}

          <h1 className="tdp-title" style={{ lineHeight: 1.05, marginTop: 4 }}>
            {safeEvent.title || "Event RSVP"}
          </h1>
          {safeEvent.subtitle ? (
            <p className="tdp-sub" style={{ marginTop: 12, fontWeight: 700 }}>
              {safeEvent.subtitle}
            </p>
          ) : null}
          <p className="tdp-sub" style={{ marginTop: 12 }}>
            {safeEvent.partnerName ? (
              <>
                Partner: {safeEvent.partnerName}
                <br />
              </>
            ) : null}
            Date: {safeEvent.dateLabel || "TBD"}
            <br />
            Time: {safeEvent.timeLabel || "TBD"}
            <br />
            Venue: {safeEvent.venueName || safeEvent.title || "Road Home Program Event"}
            <br />
            {safeEvent.addressLines?.map((line) => (
              <React.Fragment key={line}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
          {safeEvent.audienceLabel ? (
            <p className="tdp-sub" style={{ marginTop: 12 }}>
              Audience: {safeEvent.audienceLabel}
            </p>
          ) : null}
          {safeEvent.description ? (
            <div className="tdp-block" style={{ marginTop: 16 }}>
              <p className="tdp-help" style={{ margin: 0, color: "#16324f" }}>
                {safeEvent.description}
              </p>
            </div>
          ) : null}

          {safeEvent.calendar ? (
            <AddToCalendar
              title={safeEvent.title}
              startUtc={safeEvent.calendar.startUtc}
              endUtc={safeEvent.calendar.endUtc}
              timezone={safeEvent.calendar.timezone}
              location={safeEvent.calendar.location}
              description={safeEvent.calendar.description}
              publicEventUrl={safeEvent.calendar.publicEventUrl}
            />
          ) : null}

          {!isEventClosed && hasCapacityTracking && (
            <div className="tdp-block" style={{ marginTop: 16 }}>
              <h3>Capacity</h3>
              {capacityLoading && (
                <p className="tdp-help" style={{ marginTop: 0 }}>
                  Loading live seat availability...
                </p>
              )}
              {!capacityLoading && totalSeats !== null && (
                <>
                  <div className="tdp-progress" aria-hidden="true">
                    <div className="tdp-progress-bar" style={{ width: `${pctFilled}%` }} />
                  </div>
                  <div className="tdp-progress-meta">
                    <span>{totalSeats} total seats</span>
                    <strong>{remainingSeats ?? "—"} seats left</strong>
                  </div>
                  <p className="tdp-help" style={{ marginTop: 8 }}>
                    {takenSeats ?? 0} reserved
                  </p>
                </>
              )}
              {capacityLoadError && (
                <p className="tdp-help" style={{ color: "#a12626", marginTop: 8 }}>
                  {capacityLoadError}
                </p>
              )}
              {isSoldOut && (
                <div className="tdp-msg" style={{ marginTop: 8 }}>
                  This event is currently full.
                </div>
              )}
              {!isSoldOut && isLimitedSpots && (
                <p className="tdp-help" style={{ color: "#8a5a00", marginTop: 8 }}>
                  Limited spots left. RSVP soon.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="tdp-right">
          <article className="tdp-card">
            {ticketConfirmation ? (
              <>
                <h2 className="tdp-card-title">You’re registered</h2>
                <p className="tdp-card-sub">
                  Your RSVP is confirmed. A confirmation email with one mobile ticket per attendee has been sent or queued.
                </p>
                <div className="tdp-block">
                  <h3>Mobile tickets</h3>
                  <ul style={{ display: "grid", gap: 12, paddingLeft: 20 }}>
                    {(ticketConfirmation.tickets || []).map((ticket, index) => (
                      <li key={ticket.ticket_id || ticket.ticket_code || index}>
                        <strong>{ticket.attendee_name || `Attendee ${index + 1}`}</strong>
                        <br />
                        Ticket code: <code>{ticket.ticket_code}</code>
                        <br />
                        <a href={ticket.mobile_ticket_url}>View Mobile Ticket</a>
                        {ticket.cancel_url ? (
                          <>
                            {" · "}
                            <a href={ticket.cancel_url}>Cancel ticket</a>
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
                {ticketConfirmation.manage_rsvp_url ? (
                  <p>
                    <a href={ticketConfirmation.manage_rsvp_url}>Manage this RSVP</a>
                  </p>
                ) : null}
                <p className="tdp-help">
                  Save your ticket links. For help, email{" "}
                  <a href="mailto:events@roadhomeprogram.org">events@roadhomeprogram.org</a>.
                </p>
              </>
            ) : isEventClosed ? (
              <ClosedEventMessage
                eventName={safeEvent.title}
                nextEventLink="/sandiego-salute-and-social"
                nextEventLabel="Check Out Upcoming Events"
              />
            ) : slotLookupLoading ? (
              <>
                <h2 className="tdp-card-title">RSVP</h2>
                <p className="tdp-card-sub">Loading event availability...</p>
              </>
            ) : isRsvpUnavailable ? (
              <>
                <h2 className="tdp-card-title">{unavailableLabel}</h2>
                <p className="tdp-card-sub">{unavailableMessage}</p>
                {unavailableCtaHref && unavailableCtaLabel ? (
                  <a
                    className="tdp-submit"
                    href={unavailableCtaHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-flex" }}
                  >
                    {unavailableCtaLabel}
                  </a>
                ) : null}
              </>
            ) : (
              <>
                <h2 className="tdp-card-title">RSVP</h2>
                <p className="tdp-card-sub">
                  General Admission is preselected for this event.
                </p>

                <form className="tdp-form" onSubmit={handleSubmit} noValidate>
                  <fieldset disabled={submitting} style={{ border: 0, padding: 0, margin: 0 }}>
                {attendees.map((attendee, index) => {
                  const prefix = `attendees.${index}`;
                  const isServiceStatus = SERVICE_STATUSES.has(attendee.status);
                  const showFullProfileFields =
                    !hostedTicketGuestsUseTicketFieldsOnly || index === 0;
                  return (
                    <div
                      key={`attendee-${index}`}
                      className="tdp-block"
                      style={{ marginBottom: 16 }}
                    >
                      {isMultiMode && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <h3 style={{ margin: 0 }}>Attendee {index + 1}</h3>
                          <button
                            type="button"
                            className="tdp-ghost-btn tdp-ghost-btn--danger"
                            onClick={() => removeAttendee(index)}
                            disabled={attendees.length <= 1}
                          >
                            Remove
                          </button>
                        </div>
                      )}

                      <div className="tdp-row" style={{ marginTop: isMultiMode ? 12 : 0 }}>
                        <label data-field={`${prefix}.firstName`}>
                          First name*
                          <input
                            value={attendee.firstName}
                            onChange={(e) => updateAttendeeField(index, "firstName", e.target.value)}
                            aria-invalid={!!errors[`${prefix}.firstName`]}
                          />
                          {errors[`${prefix}.firstName`] && (
                            <div className="tdp-err">{errors[`${prefix}.firstName`]}</div>
                          )}
                        </label>
                        <label data-field={`${prefix}.lastName`}>
                          Last name*
                          <input
                            value={attendee.lastName}
                            onChange={(e) => updateAttendeeField(index, "lastName", e.target.value)}
                            aria-invalid={!!errors[`${prefix}.lastName`]}
                          />
                          {errors[`${prefix}.lastName`] && (
                            <div className="tdp-err">{errors[`${prefix}.lastName`]}</div>
                          )}
                        </label>
                      </div>

                      <div className="tdp-row">
                        <label data-field={`${prefix}.email`}>
                          Email*
                          <input
                            value={attendee.email}
                            onChange={(e) => updateAttendeeField(index, "email", e.target.value)}
                            aria-invalid={!!errors[`${prefix}.email`]}
                          />
                          {errors[`${prefix}.email`] && (
                            <div className="tdp-err">{errors[`${prefix}.email`]}</div>
                          )}
                        </label>
                        {showFullProfileFields && (
                          <label data-field={`${prefix}.phone`}>
                            Phone*
                            <input
                              value={attendee.phone}
                              onChange={(e) => onPhoneChange(index, e.target.value)}
                              placeholder="(###) ###-####"
                              aria-invalid={!!errors[`${prefix}.phone`]}
                            />
                            {errors[`${prefix}.phone`] && (
                              <div className="tdp-err">{errors[`${prefix}.phone`]}</div>
                            )}
                          </label>
                        )}
                      </div>

                      {showFullProfileFields && (
                        <label data-field={`${prefix}.status`} className="tdp-status">
                          Status*
                          <select
                            value={attendee.status}
                            onChange={(e) => handleStatusChange(index, e.target.value)}
                            aria-invalid={!!errors[`${prefix}.status`]}
                          >
                            {formStatusOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          {errors[`${prefix}.status`] && (
                            <div className="tdp-err">{errors[`${prefix}.status`]}</div>
                          )}
                        </label>
                      )}

                      {showFullProfileFields && isServiceStatus && (
                        <>
                          <div
                            data-field={`${prefix}.branches`}
                            className={`tdp-section ${errors[`${prefix}.branches`] ? "tdp-err-ring" : ""}`}
                          >
                            <MultiChipGroup
                              label="Branch of Service (select all that apply)*"
                              options={BRANCHES}
                              values={attendee.branches}
                              onToggle={(option) => toggleAttendeeChip(index, "branches", option)}
                              idPrefix={`${safeEvent.slug || "event"}-attendee-${index + 1}-branch`}
                            />
                            {errors[`${prefix}.branches`] && (
                              <div className="tdp-err">{errors[`${prefix}.branches`]}</div>
                            )}
                          </div>

                          <div
                            data-field={`${prefix}.eras`}
                            className={`tdp-section ${errors[`${prefix}.eras`] ? "tdp-err-ring" : ""}`}
                          >
                            <MultiChipGroup
                              label="Service Era (select all that apply)"
                              options={ERAS}
                              values={attendee.eras}
                              onToggle={(option) => toggleAttendeeChip(index, "eras", option)}
                              idPrefix={`${safeEvent.slug || "event"}-attendee-${index + 1}-era`}
                            />
                            {errors[`${prefix}.eras`] && (
                              <div className="tdp-err">{errors[`${prefix}.eras`]}</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {isMultiMode && (
                  <button
                    type="button"
                    className="tdp-ghost-btn tdp-ghost-btn--add"
                    onClick={addAttendee}
                    disabled={attendees.length >= maxAttendees}
                    style={{ marginBottom: 16 }}
                  >
                    Add another attendee
                  </button>
                )}

                {isFamilyMode && (
                  <label data-field="partySize" className="tdp-status">
                    {safeEvent.partySizeLabel || "Party size*"}
                    <select
                      value={partySize}
                      disabled={isSoldOut}
                      onChange={(e) => {
                        setPartySize(Number(e.target.value));
                        if (errors.partySize) {
                          setErrors((prev) => ({ ...prev, partySize: undefined }));
                        }
                      }}
                      aria-invalid={!!errors.partySize}
                    >
                      {Array.from({ length: maxPartySize }, (_, idx) => idx + 1).map((size) => (
                        <option
                          key={size}
                          value={size}
                          disabled={remainingSeats !== null && size > maxPartyByRemaining}
                        >
                          {size}
                        </option>
                      ))}
                    </select>
                    {errors.partySize && <div className="tdp-err">{errors.partySize}</div>}
                    {!errors.partySize && partySizeOverRemaining && remainingSeats !== null && (
                      <div className="tdp-err">
                        Only {remainingSeats} seat{remainingSeats === 1 ? "" : "s"} remaining.
                      </div>
                    )}
                    {safeEvent.partySizeHelp ? (
                      <div className="tdp-help" style={{ marginTop: 8 }}>
                        {safeEvent.partySizeHelp}
                      </div>
                    ) : null}
                  </label>
                )}

                <label className="tdp-check" data-field="consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      if (errors.consent) {
                        setErrors((prev) => ({ ...prev, consent: undefined }));
                      }
                    }}
                  />
                  <span>I confirm this RSVP is accurate.</span>
                </label>
                {errors.consent && <div className="tdp-err">{errors.consent}</div>}

                <label className="tdp-check" style={{ marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={addToMailingList}
                    onChange={(e) => setAddToMailingList(e.target.checked)}
                  />
                  <span>
                    {safeEvent.mailingListLabel || "Add me to the Road Home Program mailing list."}
                  </span>
                </label>

                {message && (
                  <div className="tdp-msg" role="status" aria-live="polite">
                    {message}
                    {messageCode === "DUPLICATE_RSVP" && (
                      <button
                        type="button"
                        className="tdp-ghost-btn"
                        onClick={() => window.location.reload()}
                        style={{ marginTop: 8 }}
                      >
                        Refresh page
                      </button>
                    )}
                  </div>
                )}

                <div id="turnstile-container" style={{ height: 0, overflow: "hidden" }} />

                <button
                  className="tdp-submit"
                  type="submit"
                  disabled={submitting || isSoldOut || partySizeOverRemaining}
                >
                  {submitting ? "Submitting..." : "Submit RSVP"}
                </button>
                  </fieldset>
                </form>
              </>
            )}

            <p className="tdp-help" style={{ marginTop: 16 }}>
              Need assistance? Email{" "}
              <a href="mailto:events@roadhomeprogram.org">
                events@roadhomeprogram.org
              </a>{" "}
              or call <a href="tel:13129428387">(312) 942-8387 (VETS)</a>.
            </p>
          </article>
        </section>

        {showTeamSection ? (
          <section
            className={isSaluteSocialPage ? "tdp-left tdp-left--team" : "tdp-left"}
          >
            <div style={{ marginTop: isSaluteSocialPage ? 0 : 24 }}>
              <SdTeamSection
                title="Meet the Team You’ll See at This Event"
                intro="Our Veteran and Family Outreach Team will be on site to help with questions, welcome guests, and connect attendees to Road Home Program resources."
                members={safeEvent.teamMembers}
              />
            </div>
          </section>
        ) : null}
      </main>

      <footer className="tdp-footer" role="contentinfo">
        <div className="tdp-footer__inner">
          <div className="tdp-footer__brand">
            <img
              src={ShieldFooterIMG}
              alt=""
              aria-hidden="true"
              className="tdp-footer__shield"
              decoding="async"
            />
            <div className="tdp-footer__brandtext">
              <div className="tdp-footer__title">Road Home Program</div>
              <div className="tdp-footer__org">
                The National Center of Excellence for Veterans and Their Families
                at Rush
              </div>
              <div className="tdp-footer__contact">
                1645 W. Jackson Blvd., Suite 602, Chicago, IL 60612
                <span className="tdp-dot" aria-hidden>
                  •
                </span>
                <a href="tel:13129428387">(312) 942-8387 (VETS)</a>
              </div>
            </div>
          </div>

          <nav className="tdp-footer__nav" aria-label="Footer">
            <a href="https://roadhomeprogram.org/family-center/">Help for Families</a>
            <a href="https://roadhomeprogram.org/get-care/">Get Care</a>
            <a href="https://roadhomeprogram.org/accelerated-treatment-program/">
              Accelerated Treatment Program
            </a>
            <a href="https://roadhomeprogram.org/outpatient-program/">
              Outpatient Program
            </a>
            <a href="https://roadhomeprogram.org/outreach-and-events/">
              Outreach and Events
            </a>
            <a href="https://roadhomeprogram.org/contact-us/">General Information</a>
            <a
              href="https://www.rush.edu/website-privacy-statement"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Statement
            </a>
            <a href="https://www.rush.edu/disclaimer" target="_blank" rel="noreferrer">
              Disclaimer
            </a>
            <a
              href="https://www.rush.edu/sites/default/files/rush-nondiscrimination-policy.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Nondiscrimination Policy
            </a>
          </nav>

          <div className="tdp-footer__legal">
            © {new Date().getFullYear()} Road Home Program at Rush.
          </div>
        </div>
      </footer>
    </>
  );
}
